/**
 * Data hooks (TanStack Query) — the only way pages read or change data.
 *   UI → these hooks → services → DataProvider → Mock | API
 */
import { useMemo } from 'react';
import { keepPreviousData, useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Backup } from '@/types/models';
import type { ManagementAnalytics, ManagementFilter } from '@/domain/analytics/types';
import { computeManagementAnalytics } from '@/domain/analytics/management';
import type { Services } from '@/services/services';
import { useServices, useToast } from '@/app/AppContext';

export const qk = {
  projects: ['projects'] as const,
  members: ['members'] as const,
  workLogs: ['workLogs'] as const,
  projectUpdates: ['projectUpdates'] as const,
  settings: ['settings'] as const,
  report: (date: string) => ['report', date] as const,
  management: (f: ManagementFilter) => ['analytics', 'management', f] as const,
};

export interface DataState<T> {
  data: T | undefined;
  isPending: boolean;
  error: Error | null;
  /** true while a background refresh runs (data is still shown) */
  isFetching: boolean;
  refetch: () => void;
}

/**
 * The five collections every page of the original app read from. Each is a separate cached query,
 * so a page that needs only some of them can use the single-resource hooks below.
 */
export function useDataset(): DataState<Backup> {
  const s = useServices();
  const r = useQueries({
    queries: [
      { queryKey: qk.projects, queryFn: () => s.projects.list() },
      { queryKey: qk.members, queryFn: () => s.members.list() },
      { queryKey: qk.workLogs, queryFn: () => s.workLogs.list() },
      { queryKey: qk.projectUpdates, queryFn: () => s.projectUpdates.list() },
      { queryKey: qk.settings, queryFn: () => s.settings.get() },
    ],
  });
  const [p, m, l, u, st] = r;
  const data = useMemo<Backup | undefined>(
    () => (p.data && m.data && l.data && u.data && st.data ? { projects: p.data, members: m.data, logs: l.data, updates: u.data, settings: st.data } : undefined),
    [p.data, m.data, l.data, u.data, st.data],
  );
  const error = (r.find((x) => x.error)?.error as Error | undefined) || null;
  return {
    data,
    isPending: !data && !error,
    error: data ? null : error,
    isFetching: r.some((x) => x.isFetching),
    refetch: () => r.forEach((x) => x.error && x.refetch()),
  };
}

export const useProjects = () => {
  const s = useServices();
  return useQuery({ queryKey: qk.projects, queryFn: () => s.projects.list() });
};
export const useMembers = () => {
  const s = useServices();
  return useQuery({ queryKey: qk.members, queryFn: () => s.members.list() });
};
export const useSettings = () => {
  const s = useServices();
  return useQuery({ queryKey: qk.settings, queryFn: () => s.settings.get() });
};

/** The Daily Report for one date (only that day's logs and Project Logs are requested). */
export const useDailyReport = (date: string) => {
  const s = useServices();
  return useQuery({ queryKey: qk.report(date), queryFn: () => s.reports.getDailyReport(date), enabled: !!date, placeholderData: keepPreviousData });
};

/**
 * Management View analytics. With server-side analytics the backend computes them per filter;
 * otherwise they are computed here from the cached collections (no extra requests when the filter changes).
 */
export function useManagementAnalytics(filter: ManagementFilter): DataState<ManagementAnalytics> & { computedAt: number } {
  const s = useServices();
  const server = s.provider.capabilities.serverAnalytics;
  const q = useQuery({ queryKey: qk.management(filter), queryFn: () => s.analytics.getManagementAnalytics(filter), enabled: server });
  const ds = useDataset();
  const local = useMemo(() => (!server && ds.data ? computeManagementAnalytics(ds.data, filter) : undefined), [server, ds.data, filter]);
  // when the figures were last (re)computed, for the "Data as of" line
  const computedAt = useMemo(() => (local || q.data ? Date.now() : 0), [local, q.data]);
  if (server) return { data: q.data, isPending: q.isPending, error: q.error, isFetching: q.isFetching, refetch: () => void q.refetch(), computedAt };
  return { ...ds, data: local, computedAt };
}

/**
 * Runs a write through the services. The success toast appears only after the provider confirms;
 * on failure the error toast shows the provider's message and `run` resolves to `undefined`.
 * Every write refreshes all cached data, because the backend may cascade (e.g. deleting a project
 * deletes its logs; logging time adds the member to the project) — exactly as the original re-rendered.
 */
export function useAction<A extends unknown[], R>(fn: (s: Services, ...args: A) => Promise<R>, success?: string | ((r: R, ...args: A) => string)) {
  const s = useServices();
  const qc = useQueryClient();
  const toast = useToast();
  const m = useMutation({
    mutationFn: (args: A) => fn(s, ...args),
    onSuccess: (r, args) => {
      if (success) toast(typeof success === 'function' ? success(r, ...args) : success);
      return qc.invalidateQueries();
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'Something went wrong. Please try again.', true),
  });
  return {
    run: async (...args: A): Promise<R | undefined> => {
      try {
        return await m.mutateAsync(args);
      } catch {
        return undefined;
      }
    },
    pending: m.isPending,
  };
}
